import type { ITicketConfig } from '@/shared/api/types/ticket-config-types'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { useLanguagesQuery, useTenantsQuery } from '@/entities/tenant'
import { TICKET_CONFIG_QUERY_KEYS, useTicketConfigsQuery } from '@/entities/ticket-config'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useLanguageFilter } from '@/shared/hooks/use-language-filter'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { Button } from '@/shared/ui/button'
import { DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { TicketConfigListTable } from '@/features/ticket-config/ticket-config-list-table'

export function TicketConfigListPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const { activeTenantId = '', isSuperAdmin, isTenantAdmin, setActiveTenantId } = useAdminScope()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const languagesQuery = useLanguagesQuery(true)
  const { selectedLanguageCode } = useLanguageFilter(languagesQuery.data?.data)

  const { data: tenantsResponse } = useTenantsQuery(
    { page: 1, pageSize: DEFAULT_PAGE_SIZE, isActive: true },
    { enabled: isSuperAdmin },
  )

  const tenantOptions = useMemo(
    () =>
      (tenantsResponse?.data ?? [])
        .filter((tenant) => tenant.name !== 'Default')
        .map((tenant) => ({ label: tenant.name, value: tenant.id })),
    [tenantsResponse?.data],
  )

  const currentTenantId = useMemo(() => {
    if (isTenantAdmin) return activeTenantId
    if (activeTenantId && tenantOptions.some((opt) => opt.value === activeTenantId))
      return activeTenantId
    return tenantOptions[0]?.value ?? ''
  }, [activeTenantId, isTenantAdmin, tenantOptions])

  const currentTenantName = useMemo(
    () => tenantOptions.find((item) => item.value === currentTenantId)?.label ?? '',
    [currentTenantId, tenantOptions],
  )

  const isTenantResolving = isSuperAdmin && !currentTenantId

  useLayoutEffect(() => {
    if (isTenantAdmin || !currentTenantId || currentTenantId === activeTenantId) return
    setActiveTenantId(currentTenantId)
  }, [activeTenantId, currentTenantId, isTenantAdmin, setActiveTenantId])

  const { data, isLoading } = useTicketConfigsQuery(
    currentTenantId || undefined,
    { current: page, pageSize },
    { enabled: Boolean(currentTenantId) },
  )

  const configs = data?.data.data ?? []
  const total = data?.data.total ?? 0

  function handleEdit(config: ITicketConfig) {
    const path = isSuperAdmin
      ? generatePath('/tenant/:tenantId/ticket-configs/:id/edit', {
          tenantId: currentTenantId,
          id: config.id,
        })
      : generatePath('/ticket-configs/:id/edit', { id: config.id })
    void navigate({ to: scopedAdminPath(path) as never })
  }

  function handleCreate() {
    const path = isSuperAdmin
      ? generatePath('/tenant/:tenantId/ticket-configs/create', {
          tenantId: currentTenantId,
        })
      : '/ticket-configs/create'
    void navigate({ to: scopedAdminPath(path) as never })
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('ticketConfig.title', { defaultValue: 'Ticket Configs' })}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(currentTenantName
            ? [
                {
                  label: currentTenantName,
                  path: scopedAdminPath(
                    generatePath('/tenant/:tenantId', { tenantId: currentTenantId }),
                  ),
                },
              ]
            : []),
          { label: t('ticketConfig.title', { defaultValue: 'Ticket Configs' }) },
        ]}
        actions={
          <Button disabled={isTenantResolving} onClick={handleCreate}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t('ticketConfig.create', { defaultValue: 'Create Ticket Config' })}
          </Button>
        }
      />

      <DataTableToolbar>
        {isSuperAdmin && (
          <DataTableFilterField label={t('sidebar.tenants')}>
            <Select
              {...(currentTenantId ? { value: currentTenantId } : {})}
              onValueChange={(value) => {
                setActiveTenantId(value)
                setPage(1)
                void queryClient.invalidateQueries({ queryKey: TICKET_CONFIG_QUERY_KEYS.all })
              }}
            >
              <SelectTrigger className="w-60">
                <SelectValue placeholder={t('sidebar.tenants')} />
              </SelectTrigger>
              <SelectContent>
                {tenantOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>
        )}
      </DataTableToolbar>

      <TicketConfigListTable
        data={configs}
        loading={isTenantResolving || (currentTenantId ? isLoading : false)}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage)
            setPageSize(nextPageSize)
          },
        }}
        {...(currentTenantId ? { tenantId: currentTenantId } : {})}
        languageCode={selectedLanguageCode}
        onEdit={handleEdit}
      />
    </PageContainer>
  )
}
